/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, Calendar, Sparkles } from 'lucide-react';
import ConfidenceChip from '@/components/terra/ConfidenceChip';

export default function EventTypeEfficiencyPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(180);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/terra-growth/intelligence/event-type-efficiency?days=${days}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="event-type-efficiency-page" className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239]">Terra Growth OS™ · Intelligence</p>
            <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-1">Event-Type Efficiency</h1>
            <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-1 max-w-2xl">
              Which event template moves perception fastest, and at which rung of the ladder?
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[90, 180, 365].map(d => (
              <button key={d}
                      data-testid={`ete-days-${d}`}
                      onClick={() => setDays(d)}
                      className={`text-[11px] rounded-full px-3 py-1 border ${
                        days === d ? 'bg-[#4A5D4E] text-white border-[#4A5D4E]' : 'bg-white text-[#5C5955] border-[#D8D4CE]'
                      }`}>{d}d</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Computing…
          </div>
        ) : !data || data.type_rollup.length === 0 ? (
          <div data-testid="event-type-efficiency-empty"
               className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            No closed events with attendee outcomes in this window. Close events to populate.
          </div>
        ) : (
          <>
            <RollupGrid rollup={data.type_rollup} />
            <CellsTable rows={data.rows} labels={data.perception_labels} />
          </>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

function RollupGrid({ rollup }) {
  return (
    <section data-testid="ete-rollup-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
      {rollup.map(r => (
        <article key={r.event_type}
                 data-testid={`ete-rollup-${r.event_type}`}
                 className="rounded-2xl border border-[#EBE8E3] bg-white p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <Calendar className="w-3.5 h-3.5 text-[#8C6239]" />
              <p className="text-sm text-[#2C2A29] truncate">
                {r.event_type.replace(/_/g, ' ').toLowerCase()}
              </p>
            </div>
            <ConfidenceChip code={r.confidence} sample={r.sample_count} compact />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Mini label="Movement %" value={`${r.movement_pct}%`} accent="text-[#4A5D4E]" />
            <Mini label="Best rung" value={r.best_state.replace(/_/g, ' ').toLowerCase()} small />
            <Mini label="Avg impact" value={r.avg_impact_composite ?? '—'} icon={<Sparkles className="w-3 h-3" />} />
          </div>
        </article>
      ))}
    </section>
  );
}

function Mini({ label, value, accent, icon, small }) {
  return (
    <div className="rounded-xl border border-[#EBE8E3] bg-[#FBFaf9] px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-[0.14em] text-[#8A8681] flex items-center gap-1">
        {icon} {label}
      </div>
      <div className={`${small ? 'text-[10px]' : 'text-base font-["Outfit"]'} tabular-nums text-[#2C2A29] ${accent || ''} mt-0.5 truncate`}>
        {value}
      </div>
    </div>
  );
}

function CellsTable({ rows, labels }) {
  return (
    <section className="rounded-2xl border border-[#EBE8E3] bg-white overflow-hidden">
      <header className="bg-[#F4F2EE] px-4 py-2">
        <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">By event-type × from-state</p>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-['Work_Sans']">
          <thead className="text-[10px] uppercase tracking-[0.12em] text-[#8A8681]">
            <tr>
              <th className="px-3 py-2 text-left">Event type</th>
              <th className="px-3 py-2 text-left">From state</th>
              <th className="px-3 py-2 text-right">Sample</th>
              <th className="px-3 py-2 text-right">Movement</th>
              <th className="px-3 py-2 text-right">Avg impact</th>
              <th className="px-3 py-2 text-left">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.event_type}-${r.from_state}-${i}`}
                  data-testid={`ete-row-${r.event_type}-${r.from_state}`}
                  className="border-t border-[#EBE8E3]">
                <td className="px-3 py-2 text-[#2C2A29]">{r.event_type.replace(/_/g, ' ').toLowerCase()}</td>
                <td className="px-3 py-2 text-[#5C5955]">{labels[r.from_state] || r.from_state}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.sample_count}</td>
                <td className="px-3 py-2 text-right tabular-nums text-[#4A5D4E]">{r.movement_pct}%</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.avg_impact_composite ?? '—'}</td>
                <td className="px-3 py-2"><ConfidenceChip code={r.confidence} sample={r.sample_count} compact /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
